import { apiClient } from './apiClient';

export interface ContentStats {
  daily: number;
  monthly: number;
  yearly: number;
  totalPublished: number;
}

export interface HitCountStats {
  daily: number;
  weekly: number;
  monthly: number;
  yearly: number;
  total: number;
}

export interface MediaItem {
  id: number;
  low_quality?: string;
  original?: string;
}

export interface LatestContent {
  id: number;
  title: string;
  content: string;
  createdOn: string;
  updatedOn: string;
  priority: number;
  thumbnail?: string;
  slug?: string;
  images: MediaItem[];
  videos: MediaItem[];
}

export interface Content extends LatestContent {
  isDraft: boolean;
  isRemoved: boolean;
  reportCount?: number;
}

export interface ContentListResponse {
  data: Content[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Report {
  id: number;
  type: string;
  explanation?: string;
  createdOn: string;
  owner?: {
    id: number;
    username: string;
  };
}

export const contentsService = {
  async getStats(): Promise<ContentStats> {
    return apiClient.get<ContentStats>('/contents/stats');
  },
  async getLatest(limit: number = 3): Promise<LatestContent[]> {
    return apiClient.get<LatestContent[]>(`/contents/latest?limit=${limit}`);
  },
  async getHitCountStats(): Promise<HitCountStats> {
    return apiClient.get<HitCountStats>('/contents/hitcount-stats');
  },
  async searchContents(
    search?: string,
    page: number = 1,
    limit: number = 20,
    hasReports?: boolean,
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (hasReports === true) {
      params.append('hasReports', 'true');
    }
    return apiClient.get<ContentListResponse>(`/contents?${params.toString()}`);
  },
  async deleteContent(id: number): Promise<void> {
    await apiClient.delete<void>(`/contents/${id}`);
  },
  async getContentReports(id: number): Promise<Report[]> {
    try {
      const data = await apiClient.get<Report[]>(`/contents/${id}/reports`);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('[contentsService] Error fetching reports:', error);
      return [];
    }
  },
  async triggerConversions(
    id: number,
  ): Promise<{ message: string; contentId: number }> {
    return apiClient.post<{ message: string; contentId: number }>(
      `/contents/${id}/trigger-conversions`,
    );
  },
};
